import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, LogOut, Share2, User, FileText, MessageCircle, Link2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getUnreadCount } from "@/api/agent";
import CreateDossierDialog from "@/components/CreateDossierDialog";
import { useQuestionsForMe } from "@/components/agent/PendingQuestions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  achat: "Achat — formulaire en attente",
  formulaire_ok: "Formulaire reçu",
  idle: "Nouveau",
  extracting: "Extraction...",
  stories_ready: "Histoires prêtes",
  generating: "Génération...",
  ready: "Terminé",
  error: "Erreur",
};

const STATUS_COLORS = {
  achat: "bg-purple-100 text-purple-800",
  formulaire_ok: "bg-teal-100 text-teal-800",
  idle: "bg-gray-100 text-gray-800",
  extracting: "bg-blue-100 text-blue-800",
  stories_ready: "bg-amber-100 text-amber-800",
  generating: "bg-blue-100 text-blue-800",
  ready: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
};

export default function DashboardScreen({ user, isTeam = false, onSelectClient, onNewClient, onLogout }) {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadTotal, setUnreadTotal] = useState(0);
  // Distinct du compteur de non-lus : celui-ci ne retombe que sur une
  // reponse, pas sur une simple ouverture du chat.
  const [pendingQuestions] = useQuestionsForMe();
  // Repli quand le presse-papiers refuse (Safari perd l'autorisation apres
  // l'appel reseau) : on montre le lien pour qu'il reste copiable a la main.
  const [lienARecopier, setLienARecopier] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const d = await getUnreadCount();
        if (alive) setUnreadTotal(d?.total || 0);
      } catch (_) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    loadClients();
    loadUsers();
  }, []);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data.clients || []);
    } catch (err) {
      console.error("Failed to load clients:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  const handleShare = async (sessionId, userId) => {
    try {
      await axios.post(`${API}/share`, { session_id: sessionId, user_id: userId });
      toast.success("Dossier partagé");
    } catch (err) {
      toast.error("Erreur lors du partage");
    }
  };

  // Le lien du formulaire n'etait affiche qu'une fois, a la creation du
  // dossier : ferme la fenetre sans copier et il fallait recreer un dossier.
  // Le jeton est en base, l'API sait le redonner — on le relit a la demande.
  const handleCopyFormLink = async (e, sessionId) => {
    e.stopPropagation();
    let url;
    try {
      const res = await axios.get(`${API}/formulaire/dossiers/${sessionId}`);
      url = `${window.location.origin}${res.data.form_path}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de récupérer le lien");
      return;
    }
    // Le lien est en main : un echec ici ne concerne plus que la copie.
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien du formulaire copié");
    } catch (_) {
      setLienARecopier(url);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  };

  if (!user) return null;

  const otherUsers = users.filter((u) => u.email !== user.email);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bonjour {user.name}</h1>
            <p className="text-muted-foreground">Vos dossiers clients</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {isTeam && (
              <Button variant="outline" onClick={() => navigate("/assistant")} className="relative">
                <MessageCircle className="h-4 w-4 mr-2" />
                Assistant
                {unreadTotal > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 font-semibold">
                    {unreadTotal > 9 ? "9+" : unreadTotal}
                  </span>
                )}
              </Button>
            )}
            {isTeam && (
              <Button variant="outline" onClick={() => navigate("/nouveautes")}>
                ✨ Nouveautés
              </Button>
            )}
            {pendingQuestions.length > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate("/mes-demandes")}
                className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                title="Des questions attendent ta réponse"
              >
                ❓ {pendingQuestions.length} question
                {pendingQuestions.length > 1 ? "s" : ""}
              </Button>
            )}
            <CreateDossierDialog onCreated={loadClients} />
            <Button onClick={onNewClient}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau client
            </Button>
            <Button variant="ghost" onClick={onLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Client list */}
        {loading ? (
          <p className="text-muted-foreground text-center py-12">Chargement...</p>
        ) : clients.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Aucun dossier client pour l'instant</p>
            <Button onClick={onNewClient}>
              <Plus className="h-4 w-4 mr-2" />
              Créer votre premier dossier
            </Button>
          </motion.div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {clients.map((client, i) => (
              <motion.div
                key={client.session_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => onSelectClient(client.session_id)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-semibold truncate">
                            {client.prenom_nom || "Client sans nom"}
                          </span>
                        </div>
                        {client.activite && (
                          <p className="text-sm text-muted-foreground truncate mb-2">
                            {client.activite}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs border-0 ${STATUS_COLORS[client.status] || STATUS_COLORS.idle}`}>
                            {STATUS_LABELS[client.status] || client.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(client.created_at)}
                          </span>
                          {client.is_shared && (
                            <Badge variant="outline" className="text-xs">
                              Partagé
                            </Badge>
                          )}
                        </div>
                      </div>
                      {/* Lien du formulaire, tant que le client n'a pas repondu */}
                      {client.status === "achat" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-2"
                          title="Copier le lien du formulaire"
                          onClick={(e) => handleCopyFormLink(e, client.session_id)}
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Share button */}
                      {!client.is_shared && otherUsers.length > 0 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="ml-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent onClick={(e) => e.stopPropagation()}>
                            <DialogHeader>
                              <DialogTitle>Partager avec</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-2">
                              {otherUsers.map((u) => (
                                <Button
                                  key={u.id}
                                  variant="outline"
                                  className="w-full justify-start"
                                  onClick={() => handleShare(client.session_id, u.id)}
                                >
                                  {u.name} ({u.email})
                                </Button>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!lienARecopier} onOpenChange={(o) => !o && setLienARecopier(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lien du formulaire</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            La copie automatique a été refusée par le navigateur. Sélectionne le
            lien et copie-le à la main.
          </p>
          <Input readOnly value={lienARecopier || ""} onFocus={(e) => e.target.select()} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
